import React, { useState, useEffect, useRef } from 'react';
import {
  Close as CloseIcon,
  Add as PlusIcon,
  Delete as TrashIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  DragIndicator as DragIndicatorIcon,
  AutoAwesome as PolishIcon
} from '@mui/icons-material';
import {
  MenuBook as LibraryIcon
} from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  type DragEndEvent
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { polishKnowledgeFields, type SalesPlaybookTag, type SalesPlaybook, type PlaybookLibraryType } from '../services/apiService';
import { getPlaybookNameLabel } from '../constants/labels';
import LibrarySelectPanel from './LibrarySelectPanel';
import { preparePlaybookSubmission, type PlaybookRolePayload } from '../utils/playbookFormHelpers';
import { Button } from './ui/Button';

interface SortableBulletItemProps {
  id: number;
  value: string;
  placeholder: string;
  canRemove: boolean;
  onChange: (value: string) => void;
  onRemove: () => void;
}

const SortableBulletItem: React.FC<SortableBulletItemProps> = ({ id, value, placeholder, canRemove, onChange, onRemove }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 }}
      className="flex gap-2 items-center"
    >
      <Button
        variant="tertiary"
        size="sm"
        type="button"
        {...attributes}
        {...listeners}
        className="!p-0.5 cursor-grab text-ink-disabled hover:text-ink-muted flex-shrink-0 touch-none"
      >
        <DragIndicatorIcon style={{ fontSize: '1rem' }} />
      </Button>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="flex-1 px-3 py-1.5 border border-ink-border rounded-md focus:outline-none focus:ring-2 focus:ring-signature text-sm"
      />
      {canRemove && (
        <Button
          variant="tertiary"
          size="sm"
          type="button"
          onClick={onRemove}
          className="!p-1.5 text-ink-muted hover:text-error hover:bg-error-tint flex-shrink-0"
        >
          <TrashIcon style={{ fontSize: '1rem' }} />
        </Button>
      )}
    </div>
  );
};

interface SortableQuestionItemProps {
  id: number;
  question: { text: string; tags: SalesPlaybookTag[] };
  index: number;
  canRemove: boolean;
  onTextChange: (value: string) => void;
  onRemove: () => void;
  onTagToggle: (tag: SalesPlaybookTag) => void;
  tagLabels: Record<SalesPlaybookTag, string>;
  allTags: SalesPlaybookTag[];
}

const SortableQuestionItem: React.FC<SortableQuestionItemProps> = ({
  id, question, index, canRemove, onTextChange, onRemove, onTagToggle, tagLabels, allTags
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 }}
      className="p-2 border border-hairline rounded-lg bg-surface-subtle"
    >
      <div className="flex gap-2 mb-2 items-start">
        <Button
          variant="tertiary"
          size="sm"
          type="button"
          {...attributes}
          {...listeners}
          className="!p-0.5 cursor-grab text-ink-disabled hover:text-ink-muted flex-shrink-0 mt-1 touch-none"
        >
          <DragIndicatorIcon style={{ fontSize: '1rem' }} />
        </Button>
        <input
          type="text"
          value={question.text}
          onChange={(e) => onTextChange(e.target.value)}
          placeholder={`Question ${index + 1}...`}
          className="flex-1 px-3 py-1.5 border border-ink-border rounded-md focus:outline-none focus:ring-2 focus:ring-signature bg-surface text-sm"
        />
        {canRemove && (
          <Button
            variant="tertiary"
            size="sm"
            type="button"
            onClick={onRemove}
            className="!p-1.5 text-ink-muted hover:text-error hover:bg-error-tint flex-shrink-0"
          >
            <TrashIcon style={{ fontSize: '1rem' }} />
          </Button>
        )}
      </div>
      <div className="flex flex-wrap gap-1 pl-6">
        <span className="text-xs text-ink-muted mr-1">Tags:</span>
        {allTags.map(tag => (
          <button
            key={tag}
            type="button"
            onClick={() => onTagToggle(tag)}
            className={`px-1.5 py-0.5 text-xs rounded-full border transition-colors ${
              question.tags.includes(tag)
                ? 'bg-signature-tint text-signature-strong border-signature'
                : 'bg-surface-subtle text-ink-muted border-hairline hover:bg-surface-sunken'
            }`}
          >
            {tagLabels[tag]}
          </button>
        ))}
      </div>
    </div>
  );
};

const SALES_PLAYBOOK_TAGS: SalesPlaybookTag[] = [
  'situation',
  'pain',
  'cost_math',
  'authority',
  'timeline',
  'close_trigger'
];

const TAG_LABELS: Record<SalesPlaybookTag, string> = {
  situation: 'Situation',
  pain: 'Pain',
  cost_math: 'Cost Math',
  authority: 'Authority',
  timeline: 'Timeline',
  close_trigger: 'Close Trigger'
};

interface RoleData {
  id: string;
  displayName: string;
  pitchBullets: string[];
  pitchBulletIds: string[];
  advancedBullets: string[];
  advancedBulletIds: string[];
  questions: { _id?: string; text: string; tags: SalesPlaybookTag[] }[];
  isExpanded: boolean;
}

interface AddSalesPlaybookModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: {
    playbookDisplayName: string;
    roles: PlaybookRolePayload[];
  }) => void;
  existingPlaybookNames: string[];
  editingPlaybook?: SalesPlaybook | null;
}

const AddSalesPlaybookModal: React.FC<AddSalesPlaybookModalProps> = ({
  isOpen,
  onClose,
  onSave,
  existingPlaybookNames,
  editingPlaybook
}) => {
  const [playbookDisplayName, setPlaybookDisplayName] = useState('');
  const [useMultipleRoles, setUseMultipleRoles] = useState(false);
  const [roles, setRoles] = useState<RoleData[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [polishing, setPolishing] = useState(false);
  const [polishFeedback, setPolishFeedback] = useState<string | null>(null);

  // Library selection state
  const [libraryPanelOpen, setLibraryPanelOpen] = useState(false);
  const [libraryPanelType, setLibraryPanelType] = useState<PlaybookLibraryType>('pitch');
  const [libraryTargetRoleId, setLibraryTargetRoleId] = useState<string>('');

  const contentRef = useRef<HTMLDivElement>(null);
  const lastAddedRef = useRef<HTMLDivElement>(null);

  const isEditMode = !!editingPlaybook;

  // Initialize with general role
  const createGeneralRole = (): RoleData => ({
    id: 'general',
    displayName: 'General',
    pitchBullets: [''],
    pitchBulletIds: [''],
    advancedBullets: [''],
    advancedBulletIds: [''],
    questions: [{ text: '', tags: ['situation'] }],
    isExpanded: true
  });

  const createNewRole = (): RoleData => ({
    id: `role-${Date.now()}`,
    displayName: '',
    pitchBullets: [''],
    pitchBulletIds: [''],
    advancedBullets: [''],
    advancedBulletIds: [''],
    questions: [{ text: '', tags: ['situation'] }],
    isExpanded: true
  });

  // Convert existing playbook data to modal format
  const loadPlaybookData = (playbook: SalesPlaybook) => {
    setPlaybookDisplayName(playbook.displayName);

    // Check if we have only a general role or multiple roles
    const hasOnlyGeneral = playbook.roles.length === 1 && playbook.roles[0].name === 'general';

    setUseMultipleRoles(!hasOnlyGeneral);

    if (hasOnlyGeneral) {
      // Load general role data
      const generalRole = playbook.roles[0];
      setRoles([{
        id: 'general',
        displayName: 'General',
        pitchBullets: generalRole.pitch.length > 0
          ? generalRole.pitch.map(p => p.text)
          : [''],
        pitchBulletIds: generalRole.pitch.length > 0
          ? generalRole.pitch.map(p => p._id)
          : [''],
        advancedBullets: generalRole.advanced?.length > 0
          ? generalRole.advanced.map(a => a.text)
          : [''],
        advancedBulletIds: generalRole.advanced?.length > 0
          ? generalRole.advanced.map(a => a._id)
          : [''],
        questions: generalRole.questions.length > 0
          ? generalRole.questions.map(q => ({ _id: q._id, text: q.questionText, tags: q.tags }))
          : [{ text: '', tags: ['situation'] }],
        isExpanded: true
      }]);
    } else {
      // Load ALL roles (excluding general role which is auto-created)
      const rolesToLoad = playbook.roles.filter(r => r.name !== 'general');

      if (rolesToLoad.length === 0) {
        setRoles([createNewRole()]);
      } else {
        setRoles(rolesToLoad.map(r => ({
          id: r._id,
          displayName: r.displayName,
          pitchBullets: r.pitch.length > 0 ? r.pitch.map(p => p.text) : [''],
          pitchBulletIds: r.pitch.length > 0 ? r.pitch.map(p => p._id) : [''],
          advancedBullets: r.advanced?.length > 0 ? r.advanced.map(a => a.text) : [''],
          advancedBulletIds: r.advanced?.length > 0 ? r.advanced.map(a => a._id) : [''],
          questions: r.questions.length > 0
            ? r.questions.map(q => ({ _id: q._id, text: q.questionText, tags: q.tags }))
            : [{ text: '', tags: ['situation'] }],
          isExpanded: true
        })));
      }
    }
  };

  useEffect(() => {
    if (isOpen) {
      setError(null);
      setSaving(false);
      setPolishing(false);
      setPolishFeedback(null);

      if (editingPlaybook) {
        loadPlaybookData(editingPlaybook);
      } else {
        // Reset form for new sales playbook
        setPlaybookDisplayName('');
        setUseMultipleRoles(false);
        setRoles([createGeneralRole()]);
      }
    }
    // loadPlaybookData is a plain function rebuilt on every render; listing it would re-run this
    // open/reset effect continuously. It only reads the editingPlaybook already in the deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, editingPlaybook]);

  // Scroll to bottom when adding new items
  const scrollToBottom = () => {
    setTimeout(() => {
      if (contentRef.current) {
        contentRef.current.scrollTop = contentRef.current.scrollHeight;
      }
    }, 100);
  };

  const handleRoleModeChange = (useMultiple: boolean) => {
    setUseMultipleRoles(useMultiple);
    if (useMultiple) {
      // Start with one empty role
      setRoles([createNewRole()]);
    } else {
      // Reset to general role
      setRoles([createGeneralRole()]);
    }
  };

  const handleAddRole = () => {
    setRoles([...roles, createNewRole()]);
    scrollToBottom();
  };

  const handleRemoveRole = (roleId: string) => {
    if (roles.length > 1) {
      setRoles(roles.filter(r => r.id !== roleId));
    }
  };

  const handleRoleNameChange = (roleId: string, displayName: string) => {
    setRoles(roles.map(r =>
      r.id === roleId ? { ...r, displayName } : r
    ));
  };

  const toggleRoleExpanded = (roleId: string) => {
    setRoles(roles.map(r =>
      r.id === roleId ? { ...r, isExpanded: !r.isExpanded } : r
    ));
  };

  // Pitch bullet handlers
  const handleAddPitchBullet = (roleId: string) => {
    setRoles(roles.map(r =>
      r.id === roleId
        ? { ...r, pitchBullets: [...r.pitchBullets, ''], pitchBulletIds: [...r.pitchBulletIds, ''] }
        : r
    ));
    scrollToBottom();
  };

  const handleRemovePitchBullet = (roleId: string, index: number) => {
    setRoles(roles.map(r =>
      r.id === roleId && r.pitchBullets.length > 1
        ? {
          ...r,
          pitchBullets: r.pitchBullets.filter((_, i) => i !== index),
          pitchBulletIds: r.pitchBulletIds.filter((_, i) => i !== index)
        }
        : r
    ));
  };

  const handlePitchBulletChange = (roleId: string, index: number, value: string) => {
    setRoles(roles.map(r => {
      if (r.id !== roleId) return r;
      const newBullets = [...r.pitchBullets];
      newBullets[index] = value;
      return { ...r, pitchBullets: newBullets };
    }));
  };

  // Advanced bullet handlers
  const handleAddAdvancedBullet = (roleId: string) => {
    setRoles(roles.map(r =>
      r.id === roleId
        ? { ...r, advancedBullets: [...r.advancedBullets, ''], advancedBulletIds: [...r.advancedBulletIds, ''] }
        : r
    ));
    scrollToBottom();
  };

  const handleRemoveAdvancedBullet = (roleId: string, index: number) => {
    setRoles(roles.map(r =>
      r.id === roleId && r.advancedBullets.length > 1
        ? {
          ...r,
          advancedBullets: r.advancedBullets.filter((_, i) => i !== index),
          advancedBulletIds: r.advancedBulletIds.filter((_, i) => i !== index)
        }
        : r
    ));
  };

  const handleAdvancedBulletChange = (roleId: string, index: number, value: string) => {
    setRoles(roles.map(r => {
      if (r.id !== roleId) return r;
      const newBullets = [...r.advancedBullets];
      newBullets[index] = value;
      return { ...r, advancedBullets: newBullets };
    }));
  };

  // Question handlers
  const handleAddQuestion = (roleId: string) => {
    setRoles(roles.map(r =>
      r.id === roleId
        ? { ...r, questions: [...r.questions, { text: '', tags: ['situation'] as SalesPlaybookTag[] }] }
        : r
    ));
    scrollToBottom();
  };

  const handleRemoveQuestion = (roleId: string, index: number) => {
    setRoles(roles.map(r =>
      r.id === roleId && r.questions.length > 1
        ? { ...r, questions: r.questions.filter((_, i) => i !== index) }
        : r
    ));
  };

  const handleQuestionTextChange = (roleId: string, index: number, value: string) => {
    setRoles(roles.map(r => {
      if (r.id !== roleId) return r;
      const newQuestions = [...r.questions];
      newQuestions[index] = { ...newQuestions[index], text: value };
      return { ...r, questions: newQuestions };
    }));
  };

  const handleQuestionTagToggle = (roleId: string, index: number, tag: SalesPlaybookTag) => {
    setRoles(roles.map(r => {
      if (r.id !== roleId) return r;
      const newQuestions = [...r.questions];
      const currentTags = newQuestions[index].tags;
      if (currentTags.includes(tag)) {
        if (currentTags.length > 1) {
          newQuestions[index] = { ...newQuestions[index], tags: currentTags.filter(t => t !== tag) };
        }
      } else {
        newQuestions[index] = { ...newQuestions[index], tags: [...currentTags, tag] };
      }
      return { ...r, questions: newQuestions };
    }));
  };

  const openLibraryPanel = (roleId: string, type: PlaybookLibraryType) => {
    setLibraryTargetRoleId(roleId);
    setLibraryPanelType(type);
    setLibraryPanelOpen(true);
  };

  const handleLibrarySelect = (texts: string[]) => {
    if (!libraryTargetRoleId || texts.length === 0) return;

    setRoles(roles.map(r => {
      if (r.id !== libraryTargetRoleId) return r;

      if (libraryPanelType === 'pitch') {
        const retainedIndices = r.pitchBullets.flatMap((text, index) => text.trim() ? [index] : []);
        return {
          ...r,
          pitchBullets: [...retainedIndices.map(index => r.pitchBullets[index]), ...texts],
          pitchBulletIds: [...retainedIndices.map(index => r.pitchBulletIds[index]), ...texts.map(() => '')]
        };
      }
      if (libraryPanelType === 'advanced') {
        const retainedIndices = r.advancedBullets.flatMap((text, index) => text.trim() ? [index] : []);
        return {
          ...r,
          advancedBullets: [...retainedIndices.map(index => r.advancedBullets[index]), ...texts],
          advancedBulletIds: [...retainedIndices.map(index => r.advancedBulletIds[index]), ...texts.map(() => '')]
        };
      }
      if (libraryPanelType === 'question') {
        const existing = r.questions.filter(q => q.text.trim());
        const newQuestions = texts.map(text => ({
          text,
          tags: ['situation'] as SalesPlaybookTag[]
        }));
        return { ...r, questions: [...existing, ...newQuestions] };
      }
      return r;
    }));
  };

  const handlePitchDragEnd = (roleId: string, event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setRoles(prev => prev.map(r => {
      if (r.id !== roleId) return r;
      return {
        ...r,
        pitchBullets: arrayMove(r.pitchBullets, active.id as number, over.id as number),
        pitchBulletIds: arrayMove(r.pitchBulletIds, active.id as number, over.id as number)
      };
    }));
  };

  const handleAdvancedDragEnd = (roleId: string, event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setRoles(prev => prev.map(r => {
      if (r.id !== roleId) return r;
      return {
        ...r,
        advancedBullets: arrayMove(r.advancedBullets, active.id as number, over.id as number),
        advancedBulletIds: arrayMove(r.advancedBulletIds, active.id as number, over.id as number)
      };
    }));
  };

  const handleQuestionDragEnd = (roleId: string, event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setRoles(prev => prev.map(r => {
      if (r.id !== roleId) return r;
      return { ...r, questions: arrayMove(r.questions, active.id as number, over.id as number) };
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const submission = preparePlaybookSubmission(playbookDisplayName, roles, useMultipleRoles);
    if (submission.kind === 'invalid') {
      setError(submission.error);
      return;
    }

    setSaving(true);
    try {
      await onSave(submission.payload);
    } catch (err: any) {
      setError(err.message || 'Failed to save sales playbook');
      setSaving(false);
    }
  };

  const handlePolish = async () => {
    const fields = roles.flatMap((role) => [
      ...role.pitchBullets.map((text, index) => ({
        id: `${role.id}:pitch:${index}`,
        text,
        contentRole: 'agent-talk-track' as const,
      })),
      ...role.advancedBullets.map((text, index) => ({
        id: `${role.id}:advanced:${index}`,
        text,
        contentRole: 'agent-talk-track' as const,
      })),
      ...role.questions.map((question, index) => ({
        id: `${role.id}:question:${index}`,
        text: question.text,
        contentRole: 'agent-talk-track' as const,
      })),
    ]).filter((field) => field.text.trim());

    if (fields.length === 0) {
      setPolishFeedback('Add some playbook content before polishing.');
      return;
    }

    setPolishing(true);
    setError(null);
    setPolishFeedback(null);
    try {
      const polished = await polishKnowledgeFields('playbook', fields);
      const byId = new Map(polished.map((field) => [field.id, field.text]));
      setRoles((currentRoles) => currentRoles.map((role) => ({
        ...role,
        pitchBullets: role.pitchBullets.map((text, index) => byId.get(`${role.id}:pitch:${index}`) || text),
        advancedBullets: role.advancedBullets.map((text, index) => byId.get(`${role.id}:advanced:${index}`) || text),
        questions: role.questions.map((question, index) => ({
          ...question,
          text: byId.get(`${role.id}:question:${index}`) || question.text,
        })),
      })));
      setPolishFeedback(
        polished.some((field) => field.changed)
          ? 'Polished wording applied. Review it before saving.'
          : 'This wording is already ready for a live call.'
      );
    } catch (polishError) {
      console.error('Error polishing playbook:', polishError);
      setPolishFeedback('Could not polish this content. Your original wording is unchanged.');
    } finally {
      setPolishing(false);
    }
  };

  if (!isOpen) return null;

  const renderRoleContent = (role: RoleData) => (
    <div className="space-y-4 mt-3">
      {/* Intro Pitch */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-medium text-ink-secondary">Intro Pitch</label>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => openLibraryPanel(role.id, 'pitch')}
              className="text-ink-secondary hover:text-ink text-xs flex items-center gap-1"
            >
              <LibraryIcon style={{ fontSize: '0.875rem' }} />
              From Library
            </button>
            <button
              type="button"
              onClick={() => handleAddPitchBullet(role.id)}
              className="text-signature hover:text-signature-strong text-xs flex items-center gap-1"
            >
              <PlusIcon style={{ fontSize: '0.875rem' }} />
              Add Intro Pitch
            </button>
          </div>
        </div>
        <DndContext
          collisionDetection={closestCenter}
          onDragEnd={(event) => handlePitchDragEnd(role.id, event)}
        >
          <SortableContext
            items={role.pitchBullets.map((_, i) => i)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-2">
              {role.pitchBullets.map((bullet, index) => (
                <SortableBulletItem
                  key={index}
                  id={index}
                  value={bullet}
                  placeholder={`Pitch point ${index + 1}...`}
                  canRemove={role.pitchBullets.length > 1}
                  onChange={(value) => handlePitchBulletChange(role.id, index, value)}
                  onRemove={() => handleRemovePitchBullet(role.id, index)}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      </div>

      {/* Qualifying Questions */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-medium text-ink-secondary">Qualifying Questions</label>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => openLibraryPanel(role.id, 'question')}
              className="text-ink-secondary hover:text-ink text-xs flex items-center gap-1"
            >
              <LibraryIcon style={{ fontSize: '0.875rem' }} />
              From Library
            </button>
            <button
              type="button"
              onClick={() => handleAddQuestion(role.id)}
              className="text-signature hover:text-signature-strong text-xs flex items-center gap-1"
            >
              <PlusIcon style={{ fontSize: '0.875rem' }} />
              Add Question
            </button>
          </div>
        </div>
        <DndContext
          collisionDetection={closestCenter}
          onDragEnd={(event) => handleQuestionDragEnd(role.id, event)}
        >
          <SortableContext
            items={role.questions.map((_, i) => i)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-3">
              {role.questions.map((question, index) => (
                <SortableQuestionItem
                  key={index}
                  id={index}
                  question={question}
                  index={index}
                  canRemove={role.questions.length > 1}
                  onTextChange={(value) => handleQuestionTextChange(role.id, index, value)}
                  onRemove={() => handleRemoveQuestion(role.id, index)}
                  onTagToggle={(tag) => handleQuestionTagToggle(role.id, index, tag)}
                  tagLabels={TAG_LABELS}
                  allTags={SALES_PLAYBOOK_TAGS}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      </div>

      {/* Advanced */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-medium text-ink-secondary">Deal Progression</label>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => openLibraryPanel(role.id, 'advanced')}
              className="text-ink-secondary hover:text-ink text-xs flex items-center gap-1"
            >
              <LibraryIcon style={{ fontSize: '0.875rem' }} />
              From Library
            </button>
            <button
              type="button"
              onClick={() => handleAddAdvancedBullet(role.id)}
              className="text-signature hover:text-signature-strong text-xs flex items-center gap-1"
            >
              <PlusIcon style={{ fontSize: '0.875rem' }} />
              Add Deal Progression
            </button>
          </div>
        </div>
        <DndContext
          collisionDetection={closestCenter}
          onDragEnd={(event) => handleAdvancedDragEnd(role.id, event)}
        >
          <SortableContext
            items={role.advancedBullets.map((_, i) => i)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-2">
              {role.advancedBullets.map((bullet, index) => (
                <SortableBulletItem
                  key={index}
                  id={index}
                  value={bullet}
                  placeholder={`Deal progression point ${index + 1}...`}
                  canRemove={role.advancedBullets.length > 1}
                  onChange={(value) => handleAdvancedBulletChange(role.id, index, value)}
                  onRemove={() => handleRemoveAdvancedBullet(role.id, index)}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div className="fixed inset-0 transition-opacity" aria-hidden="true" onClick={onClose}>
          <div className="absolute inset-0 bg-ink opacity-50"></div>
        </div>

        {/* Modal panel */}
        <div className="inline-block align-bottom text-left overflow-hidden transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full modal-panel">
          <form onSubmit={handleSubmit}>
            {/* Header - Fixed */}
            <div className="px-4 pt-4 pb-3 border-b border-hairline">
              <div className="flex justify-between items-center">
                <h3 className="heading-4">
                  {isEditMode ? 'Edit Sales Playbook' : 'Add Sales Playbook'}
                </h3>
                <Button variant="tertiary" size="sm" type="button" onClick={onClose} className="!p-1">
                  <CloseIcon />
                </Button>
              </div>
            </div>

            {/* Scrollable Content */}
            <div
              ref={contentRef}
              className="px-4 py-4 max-h-[65vh] overflow-y-auto"
            >
              {error && (
                <div className="mb-4 p-3 bg-error-tint text-error rounded-md text-sm">
                  {error}
                </div>
              )}

              {polishFeedback && (
                <div role="status" className="mb-4 p-3 bg-info-tint border border-info text-ink rounded-md text-sm">
                  {polishFeedback}
                </div>
              )}

              <div className="space-y-5">
                {/* Playbook Name */}
                <div>
                  <label className="block text-sm font-medium text-ink-secondary mb-1">
                    {getPlaybookNameLabel()} *
                  </label>
                  <input
                    type="text"
                    value={playbookDisplayName}
                    onChange={(e) => setPlaybookDisplayName(e.target.value)}
                    placeholder="e.g., Airport"
                    aria-label={getPlaybookNameLabel()}
                    className="w-full px-3 py-2 border border-ink-border rounded-md focus:outline-none focus:ring-2 focus:ring-signature"
                    list="existing-playbooks"
                    autoFocus
                    disabled={isEditMode}
                  />
                  {!isEditMode && (
                    <>
                      <datalist id="existing-playbooks">
                        {existingPlaybookNames.map(existingName => (
                          <option key={existingName} value={existingName} />
                        ))}
                      </datalist>
                      <p className="mt-1 text-xs text-ink-muted">
                        Type a new playbook name
                      </p>
                    </>
                  )}
                </div>

                {/* Role Mode Selection */}
                <div>
                  <label className="block text-sm font-medium text-ink-secondary mb-2">
                    Role Structure
                  </label>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="roleMode"
                        checked={!useMultipleRoles}
                        onChange={() => handleRoleModeChange(false)}
                        className="w-4 h-4 text-signature"
                      />
                      <span className="text-sm text-ink-secondary">General role only</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="roleMode"
                        checked={useMultipleRoles}
                        onChange={() => handleRoleModeChange(true)}
                        className="w-4 h-4 text-signature"
                      />
                      <span className="text-sm text-ink-secondary">Multiple roles</span>
                    </label>
                  </div>
                  <p className="mt-1 text-xs text-ink-muted">
                    {useMultipleRoles
                      ? 'Add specific roles (e.g., VP of Operations, Facilities Manager) with their own pitch and questions'
                      : 'Use a single "General" role for all pitch and questions'
                    }
                  </p>
                </div>

                {/* Roles Section */}
                {useMultipleRoles ? (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <label className="text-sm font-medium text-ink-secondary">Roles</label>
                      <Button variant="primary" size="sm" type="button" onClick={handleAddRole} className="flex items-center gap-1">
                        <PlusIcon style={{ fontSize: '1rem' }} />
                        Add Role
                      </Button>
                    </div>

                    {roles.map((role, roleIndex) => (
                      <div
                        key={role.id}
                        className="border border-ink-border rounded-lg overflow-hidden"
                        ref={roleIndex === roles.length - 1 ? lastAddedRef : null}
                      >
                        {/* Role Header */}
                        <div
                          className="bg-surface-subtle px-3 py-2 flex items-center justify-between cursor-pointer"
                          onClick={() => toggleRoleExpanded(role.id)}
                        >
                          <div className="flex items-center gap-2 flex-1">
                            {role.isExpanded ? (
                              <ExpandLessIcon className="text-ink-muted" style={{ fontSize: '1.25rem' }} />
                            ) : (
                              <ExpandMoreIcon className="text-ink-muted" style={{ fontSize: '1.25rem' }} />
                            )}
                            <input
                              type="text"
                              value={role.displayName}
                              onChange={(e) => {
                                e.stopPropagation();
                                handleRoleNameChange(role.id, e.target.value);
                              }}
                              onClick={(e) => e.stopPropagation()}
                              placeholder="Role name (e.g., VP of Operations)"
                              className="flex-1 px-2 py-1 border border-ink-border rounded text-sm bg-surface focus:outline-none focus:ring-2 focus:ring-signature"
                            />
                          </div>
                          {roles.length > 1 && (
                            <Button
                              variant="tertiary"
                              size="sm"
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveRole(role.id);
                              }}
                              className="!p-1 ml-2 text-ink-muted hover:text-error hover:bg-error-tint"
                            >
                              <TrashIcon style={{ fontSize: '1.25rem' }} />
                            </Button>
                          )}
                        </div>

                        {/* Role Content */}
                        {role.isExpanded && (
                          <div className="px-3 pb-3 bg-surface">
                            {renderRoleContent(role)}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  /* General Role Content */
                  <div className="border border-hairline rounded-lg p-4 bg-surface-subtle">
                    <h4 className="heading-4 mb-3">General Role Content</h4>
                    {roles[0] && renderRoleContent(roles[0])}
                  </div>
                )}
              </div>

              {/* Spacer for scroll */}
              <div ref={lastAddedRef} />
            </div>

            {/* Footer - Fixed */}
            <div className="bg-surface-subtle px-4 py-3 flex flex-col gap-2 border-t sm:flex-row sm:justify-between">
              <Button type="button" variant="secondary" onClick={handlePolish} disabled={saving || polishing} className="w-full sm:w-auto">
                {polishing ? 'Polishing...' : (
                  <><PolishIcon style={{ fontSize: '1rem' }} /> Polish with AI</>
                )}
              </Button>
              <div className="flex flex-col-reverse gap-2 sm:flex-row">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={onClose}
                  disabled={saving || polishing}
                  className="w-full sm:w-auto"
                >
                  Cancel
                </Button>
                <Button type="submit" variant="primary" disabled={saving || polishing} className="w-full sm:w-auto">
                  {saving ? 'Saving...' : (isEditMode ? 'Save Changes' : 'Add Sales Playbook')}
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* Library Selection Panel */}
      <LibrarySelectPanel
        type={libraryPanelType}
        isOpen={libraryPanelOpen}
        onClose={() => setLibraryPanelOpen(false)}
        onSelect={handleLibrarySelect}
      />
    </div>
  );
};

export default AddSalesPlaybookModal;
